import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { AnalyticsService } from '../services/analytics'
import { useActiveTime } from '../hooks/useActiveTime'
import { trackEmailClick, isFromEmail } from '../services/emailTracking'

interface AnalyticsProviderProps {
  children: React.ReactNode
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation()
  const emailClickTracked = useRef(false)

  // Track email click on initial load (only once)
  useEffect(() => {
    if (!emailClickTracked.current && isFromEmail()) {
      emailClickTracked.current = true
      trackEmailClick(AnalyticsService.getSessionId())
    }
  }, [])

  // Set up active time tracking
  useActiveTime({
    idleTimeout: 30000, // 30 seconds idle timeout
    heartbeatInterval: 10000, // Update every 10 seconds
    onActiveTimeUpdate: (activeTimeMs) => {
      AnalyticsService.trackActiveTime(activeTimeMs, location.pathname)
    },
    onPageLeave: (totalActiveTimeMs) => {
      AnalyticsService.trackActiveTime(totalActiveTimeMs, location.pathname)
      AnalyticsService.updateSession(totalActiveTimeMs)
    }
  })

  // Track page views on route change
  useEffect(() => {
    AnalyticsService.trackPageView(location.pathname, document.title)
  }, [location.pathname])

  // Set up global click tracking
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement

      // Find the closest interactive element
      const clickable = target.closest('a, button, [role="button"], [data-track]')
      if (!clickable) return

      const elementType = clickable.tagName.toLowerCase()
      // Prioritize data-track attribute over HTML id for element identification
      const elementId = clickable.getAttribute('data-track') || clickable.id || undefined
      const elementText = clickable.textContent?.trim().substring(0, 100)

      // Check for data attributes for resource tracking
      const resourceType = clickable.getAttribute('data-resource-type') as
        | 'contribution'
        | 'curation'
        | 'brand'
        | 'user_profile'
        | undefined
      const resourceId = clickable.getAttribute('data-resource-id') || undefined

      AnalyticsService.trackClick(
        elementType,
        elementId,
        elementText,
        resourceType,
        resourceId
      )
    }

    document.addEventListener('click', handleClick, { capture: true })

    return () => {
      document.removeEventListener('click', handleClick, { capture: true })
    }
  }, [])

  return <>{children}</>
}
